import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
    extractPeriodKeys,
    getAvailablePeriod,
    getPeriodFromKeys,
    hasAnyRows
} from '../Util/getAvailablePeriod';

const AvailablePeriodContext = createContext(null);

const emptyReport = { scope: null, keys: [], hasRows: false, responded: false };

/**
 * Período realmente disponível no backend, acumulado a partir das respostas das
 * consultas do dashboard (useChartsData reporta cada retorno). Como nenhuma
 * consulta devolve competência que não exista, a união das competências
 * retornadas nunca extrapola os dados reais.
 *
 * O acúmulo é por escopo (cidade/unidade): ao trocar de escopo as competências
 * do escopo anterior são descartadas.
 */
export const AvailablePeriodProvider = ({ children }) => {
    const [reported, setReported] = useState(emptyReport);

    const reportPeriod = useCallback((data, scope = null) => {
        const keys = extractPeriodKeys(data);
        const rows = hasAnyRows(data);

        setReported((previous) => {
            const base = previous.scope === scope ? previous : emptyReport;
            const merged = new Set(base.keys);

            keys.forEach((key) => merged.add(key));

            const addsNothing =
                base.responded &&
                merged.size === base.keys.length &&
                (base.hasRows || !rows);

            if (base === previous && addsNothing) {
                return previous;
            }

            return {
                scope,
                keys: [...merged],
                hasRows: base.hasRows || rows,
                responded: true
            };
        });
    }, []);

    const value = useMemo(() => {
        const period = getPeriodFromKeys(reported.keys);

        return {
            // Enquanto nada foi retornado, o intervalo das opções de filtro é o
            // melhor palpite disponível.
            availablePeriod: period || getAvailablePeriod(),
            // Só é ausência de dados quando alguma consulta já respondeu e
            // nenhuma trouxe competência nem linha alguma. Antes da primeira
            // resposta o indicador não anuncia ausência.
            hasData: !reported.responded || Boolean(period) || reported.hasRows,
            reportPeriod
        };
    }, [reported, reportPeriod]);

    return (
        <AvailablePeriodContext.Provider value={value}>
            {children}
        </AvailablePeriodContext.Provider>
    );
};

// Fora do provider o período exibido é o intervalo das opções de filtro.
export const useAvailablePeriod = () => {
    const context = useContext(AvailablePeriodContext);

    return {
        availablePeriod: context?.availablePeriod || getAvailablePeriod(),
        hasData: context ? context.hasData : true
    };
};

const ignorePeriod = () => {};

// Fora do provider o relato é ignorado, para que useChartsData continue
// utilizável em dashboards que não exibem o período.
export const useReportAvailablePeriod = () =>
    useContext(AvailablePeriodContext)?.reportPeriod || ignorePeriod;
